import { createContext, useContext, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'hooks/useTheme';

// Extracts a theme-based color for ripples (fallback to blue if no gradient)
const getRippleColorFromTheme = (theme) => {
  const match = theme?.match(/#([0-9A-Fa-f]{6})/g); // Grab first color stop
  return match?.[0] || '#3B82F6'; // Fallback to blue-400
};

// Trail particles for motion blur effect
const Particle = ({ x, y, id, color }) => (
  <motion.div
    key={id}
    initial={{ opacity: 1, scale: 1 }}
    animate={{ opacity: 0, scale: 0.2 }}
    transition={{ duration: 0.6, ease: 'easeOut' }}
    className="absolute rounded-full blur-sm pointer-events-none"
    style={{
      left: x,
      top: y,
      transform: 'translate(-50%, -50%)',
      width: '0.75rem',
      height: '0.75rem',
      backgroundColor: color,
    }}
  />
);

// Expanding ripple wave triggered by speed
const Ripple = ({ x, y, id, color, strength }) => {
  const sizeMap = { subtle: 40, normal: 80, wild: 140 };
  const durationMap = { subtle: 1.5, normal: 1, wild: 0.6 };

  return (
    <motion.div
      key={id}
      initial={{ opacity: 0.5, scale: 0 }}
      animate={{ opacity: 0, scale: 4 }}
      transition={{
        duration: durationMap[strength] || 1,
        ease: 'easeOut',
      }}
      className="absolute rounded-full border-2 blur-xl pointer-events-none"
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)',
        width: sizeMap[strength] || 80,
        height: sizeMap[strength] || 80,
        borderColor: `${color}80`, // transparent, watery border
        boxShadow: `0 0 40px ${color}40`,
      }}
    />
  );
};

const CursorContext = createContext();

export const CursorProvider = ({ children }) => {
  const { theme } = useTheme(); // use current gradient theme
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const [ripples, setRipples] = useState([]);
  const [cursorType, setCursorType] = useState(
    Cookies.get('cursor_type') || 'glow'
  );
  const [cursorImage, setCursorImage] = useState(
    Cookies.get('cursor_image') || null
  );
  const [effectsEnabled, setEffectsEnabled] = useState(
    Cookies.get('cursor_effects') !== 'off'
  );
  const [rippleStrength, setRippleStrength] = useState(
    Cookies.get('cursor_ripple_strength') || 'normal'
  );

  const rippleColor = getRippleColorFromTheme(theme);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      const { clientX, clientY } = e;
      const dx = clientX - lastPosRef.current.x;
      const dy = clientY - lastPosRef.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);

      setCursorPos({ x: clientX, y: clientY });
      lastPosRef.current = { x: clientX, y: clientY };

      if (!effectsEnabled) return;

      // Leave particle trails
      const pid = Date.now() + Math.random();
      setParticles((prev) => [...prev, { id: pid, x: clientX, y: clientY }]);
      setTimeout(() => {
        setParticles((prev) => prev.filter((p) => p.id !== pid));
      }, 600);

      // Spawn watery ripple if moving fast
      if (speed > 50) {
        const rid = Date.now() + Math.random();
        setRipples((prev) => [...prev, { id: rid, x: clientX, y: clientY }]);
        setTimeout(() => {
          setRipples((prev) => prev.filter((r) => r.id !== rid));
        }, 1200);
      }
    };

    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [effectsEnabled, rippleStrength]);

  useEffect(() => {
    Cookies.set('cursor_type', cursorType, { expires: 365 });
    if (cursorImage) Cookies.set('cursor_image', cursorImage, { expires: 365 });
    Cookies.set('cursor_effects', effectsEnabled ? 'on' : 'off', { expires: 365 });
    Cookies.set('cursor_ripple_strength', rippleStrength, { expires: 365 });
  }, [cursorType, cursorImage, effectsEnabled, rippleStrength]);

  const value = {
    cursorPos,
    cursorType,
    cursorImage,
    effectsEnabled,
    rippleStrength,
    setCursorType,
    setCursorImage,
    setEffectsEnabled,
    setRippleStrength,
  };

  return (
    <CursorContext.Provider value={value}>
      <div className="relative w-full h-full cursor-none">
        {children}

        {/* Watery Ripple Effect */}
        <AnimatePresence>
          {effectsEnabled &&
            ripples.map(({ id, x, y }) => (
              <Ripple
                key={id}
                x={x}
                y={y}
                id={id}
                color={rippleColor}
                strength={rippleStrength}
              />
            ))}
        </AnimatePresence>

        {/* Particle Motion Trails */}
        <AnimatePresence>
          {effectsEnabled &&
            particles.map(({ id, x, y }) => (
              <Particle key={id} x={x} y={y} id={id} color={`${rippleColor}40`} />
            ))}
        </AnimatePresence>

        {/* Main Cursor */}
        <motion.div
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
          transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {cursorType === 'glow' && (
            <div
              className="w-6 h-6 rounded-full border-2 shadow-[0_0_35px_rgba(59,130,246,0.8)] mix-blend-screen"
              style={{
                backgroundColor: `${rippleColor}20`,
                borderColor: rippleColor,
              }}
            />
          )}
          {cursorType === 'image' && cursorImage && (
            <img
              src={cursorImage}
              alt="Custom Cursor"
              className="w-8 h-8 pointer-events-none select-none"
            />
          )}
        </motion.div>
      </div>
    </CursorContext.Provider>
  );
};

export const useCursor = () => {
  const context = useContext(CursorContext);
  if (!context) throw new Error('useCursor must be used within CursorProvider');
  return context;
};
